import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { StoreService } from "@/gen/omnios/v1/store_connect";
import { transport } from "@/lib/grpc";
import { usePortalDetail } from "@/routes/portals.$id";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/portals/$id/config")({
	component: PortalConfig,
});

const storeClient = createClient(StoreService, transport);

function PortalConfig() {
	const { id } = useParams({ from: "/portals/$id/config" });
	const qc = useQueryClient();
	const { portal } = usePortalDetail();
	const { t } = useTranslation("portals");

	const { data: config } = useQuery({
		queryKey: ["store-config", id],
		queryFn: () => storeClient.getStoreConfig({ portalInstanceId: id }),
	})

	// Portal fields
	const [sfName, setSfName] = useState("");
	const [sfSlug, setSfSlug] = useState("");
	const [storeUrl, setStoreUrl] = useState("");
	const [customDomain, setCustomDomain] = useState("");

	// Store config fields
	const [currency, setCurrency] = useState("RUB");
	const [locale, setLocale] = useState("ru");
	const [taxInclusive, setTaxInclusive] = useState(true);
	const [taxRate, setTaxRate] = useState(0);
	const [checkoutMode, setCheckoutMode] = useState("standard");
	const [inventoryTracking, setInventoryTracking] = useState(false);

	// Init from portal
	useEffect(() => {
		if (!portal) return;
		setSfName(portal.name);
		setSfSlug(portal.slug);
		setStoreUrl(portal.storeUrl);
	}, [portal]);

	// Init from config
	useEffect(() => {
		if (!config) return;
		setCurrency(config.currency || "RUB");
		setLocale(config.locale || "ru");
		setTaxInclusive(config.taxInclusive);
		setTaxRate(config.taxRatePct);
		setCheckoutMode(config.checkoutMode || "standard");
		setInventoryTracking(config.inventoryTracking);
		if (config.storeUrl && !storeUrl) setStoreUrl(config.storeUrl);
	}, [config]);

	const saveMutation = useMutation({
		mutationFn: async () => {
			// Save both portal and config in parallel
			await Promise.all([
				storeClient.updatePortalInstance({
					id,
					name: sfName,
					slug: sfSlug,
					storeUrl,
					customDomain: customDomain || undefined,
				}),
				storeClient.updateStoreConfig({
					portalInstanceId: id,
					storeName: sfName,
					storeUrl,
					currency,
					locale,
					taxInclusive,
					taxRatePct: taxRate,
					checkoutMode,
					inventoryTracking,
				}),
			])
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["store-config", id] });
			qc.invalidateQueries({ queryKey: ["portal", id] });
			qc.invalidateQueries({ queryKey: ["portals"] });
			toast.success(t("settings_saved"));
		},
		onError: () => toast.error(t("settings_save_error")),
	})

	return (
		<div className="space-y-8">
			<div className="flex items-center justify-between">
				<h2 className="text-lg font-semibold">{t("settings")}</h2>
				<Button
					size="sm"
					className="gap-1.5"
					onClick={() => saveMutation.mutate()}
					disabled={saveMutation.isPending}
				>
					<Save className="h-3.5 w-3.5" />
					{saveMutation.isPending ? t("saving") : t("save")}
				</Button>
			</div>

			{/* General */}
			<FieldGroup title={t("general")}>
				<div className="grid grid-cols-2 gap-4">
					<div>
						<Label>{t("name_label")}</Label>
						<Input
							value={sfName}
							onChange={(e) => setSfName(e.target.value)}
							placeholder={t("store_name_placeholder")}
						/>
					</div>
					<div>
						<Label>{t("address_label")}</Label>
						<Input
							value={sfSlug}
							onChange={(e) => setSfSlug(e.target.value)}
							placeholder={t("slug_placeholder")}
						/>
					</div>
				</div>
				<div>
					<Label>{t("site_url")}</Label>
					<Input
						value={storeUrl}
						onChange={(e) => setStoreUrl(e.target.value)}
						placeholder={t("site_url_placeholder")}
					/>
				</div>
				<div>
					<Label>{t("custom_domain")}</Label>
					<Input
						value={customDomain}
						onChange={(e) => setCustomDomain(e.target.value)}
						placeholder={t("custom_domain_placeholder")}
					/>
					<p className="text-xs text-muted-foreground mt-1">
						{t("custom_domain_hint")}
					</p>
				</div>
			</FieldGroup>

			{/* Regional */}
			<FieldGroup title={t("region_currency")}>
				<div className="grid grid-cols-2 gap-4">
					<div>
						<Label>{t("currency")}</Label>
						<Select value={currency} onValueChange={setCurrency}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="RUB">RUB (₽)</SelectItem>
								<SelectItem value="USD">USD ($)</SelectItem>
								<SelectItem value="EUR">EUR (€)</SelectItem>
								<SelectItem value="KZT">KZT (₸)</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div>
						<Label>{t("language")}</Label>
						<Select value={locale} onValueChange={setLocale}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="ru">{t("lang_russian")}</SelectItem>
								<SelectItem value="en">{t("lang_english")}</SelectItem>
								<SelectItem value="kz">{t("lang_kazakh")}</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>
			</FieldGroup>

			{/* Commerce */}
			<FieldGroup title={t("sales")}>
				<div className="grid grid-cols-2 gap-4">
					<div>
						<Label>{t("checkout_mode")}</Label>
						<Select value={checkoutMode} onValueChange={setCheckoutMode}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="standard">
									{t("checkout_standard")}
								</SelectItem>
								<SelectItem value="quote_only">
									{t("checkout_quote_only")}
								</SelectItem>
								<SelectItem value="hybrid">{t("checkout_hybrid")}</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div>
						<Label>{t("tax_rate")}</Label>
						<Input
							type="number"
							value={taxRate}
							onChange={(e) => setTaxRate(Number(e.target.value))}
							min={0}
							max={100}
						/>
					</div>
				</div>

				<ToggleRow
					label={t("tax_inclusive")}
					description={t("tax_inclusive_desc")}
					checked={taxInclusive}
					onChange={setTaxInclusive}
				/>
				<ToggleRow
					label={t("inventory_tracking")}
					description={t("inventory_tracking_desc")}
					checked={inventoryTracking}
					onChange={setInventoryTracking}
				/>
			</FieldGroup>
		</div>
	)
}

function FieldGroup({
	title,
	children,
}: { title: string; children: React.ReactNode }) {
	return (
		<div className="space-y-4">
			<h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
				{title}
			</h3>
			<div className="space-y-4 rounded-lg border p-4">{children}</div>
		</div>
	)
}

function ToggleRow({
	label,
	description,
	checked,
	onChange,
}: {
	label: string;
	description: string;
	checked: boolean;
	onChange: (v: boolean) => void;
}) {
	return (
		<div className="flex items-center justify-between">
			<div>
				<p className="text-sm font-medium">{label}</p>
				<p className="text-xs text-muted-foreground">{description}</p>
			</div>
			<Switch checked={checked} onCheckedChange={onChange} />
		</div>
	)
}
